import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Popcorn, Timer } from 'lucide-react';
import { getFundraiserSettings } from '../lib/api';
import { easternDateTime, splitDuration } from '../lib/kickoff';

export const POPCORN_SLUG = 'popcorn-2026';
const pad = (value) => String(value).padStart(2, '0');
const toDate = (local) => (local ? easternDateTime(local.slice(0, 10), local.slice(11, 16)) : null);
const formatOpen = (date) => date.toLocaleString('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export const usePopcornSale = () => {
  const [settings, setSettings] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => { getFundraiserSettings(POPCORN_SLUG).then(setSettings).catch(() => setSettings(null)); }, []);
  useEffect(() => { const id = window.setInterval(() => setNow(new Date()), 1000); return () => window.clearInterval(id); }, []);

  const opens = useMemo(() => toDate(settings?.opensAt), [settings]);
  const closes = useMemo(() => toDate(settings?.closesAt), [settings]);
  if (!settings) return null;
  const remaining = opens ? opens - now : 0;
  const phase = remaining > 0 ? 'coming_soon' : closes && now >= closes ? 'closed' : 'open';
  return { settings, opens, phase, parts: splitDuration(Math.max(remaining, 0)) };
};

export const PopcornTeaser = ({ id }) => {
  const sale = usePopcornSale();
  if (!sale || sale.phase === 'closed') return null;
  const { settings, opens, phase, parts } = sale;
  return (
    <div className="border-l-4 border-[#f4b400] bg-[#0a1f44] text-white px-4 py-3" data-testid={`popcorn-teaser-${id}`} data-phase={phase}>
      <div className="flex items-center gap-2 font-heading uppercase tracking-[0.3em] text-[9px] text-[#f4b400]"><Popcorn className="w-3.5 h-3.5" /> Team Fundraiser · {settings.title}</div>
      {phase === 'coming_soon' ? (
        <div className="flex flex-wrap items-end justify-between gap-3 mt-1">
          <div className="font-heading uppercase tracking-widest text-[10px] text-white/70 inline-flex items-center gap-1.5"><Timer className="w-3 h-3" /> Coming soon · opens {formatOpen(opens)} ET</div>
          <div className="flex items-end gap-2.5" aria-live="off">
            {[['Days', parts.days], ['Hrs', parts.hours], ['Min', parts.minutes], ['Sec', parts.seconds]].map(([label, value]) => (
              <div key={label} className="text-center min-w-[2rem]"><div className="font-display tabular-nums leading-none text-xl" data-testid={`popcorn-teaser-${id}-${label.toLowerCase()}`}>{pad(value)}</div><div className="font-heading uppercase tracking-widest text-[8px] text-white/55">{label}</div></div>
            ))}
          </div>
        </div>
      ) : settings.orderUrl ? (
        <a href={settings.orderUrl} target="_blank" rel="noopener noreferrer" data-testid={`popcorn-teaser-${id}-order-link`} className="mt-2 inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-[10px] px-4 py-2.5 transition-colors"><ExternalLink className="w-3.5 h-3.5" /> Order Popcorn Now</a>
      ) : (
        <div className="mt-2 font-heading uppercase tracking-widest text-[10px] text-[#f4b400]" data-testid={`popcorn-teaser-${id}-open-no-link`}>Sale is open · order link posting shortly</div>
      )}
    </div>
  );
};

export const PopcornCountdownCard = () => {
  const sale = usePopcornSale();
  if (!sale) return null;
  const { settings, opens, phase, parts } = sale;

  return (
    <figure className="min-w-0 col-span-2 sm:col-span-3 flex flex-col sm:flex-row sm:items-center sm:gap-8 border border-white/20 bg-[#071226] p-4 sm:p-6" data-testid="popcorn-card" data-phase={phase}>
      <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <div className="font-heading uppercase tracking-[0.3em] text-[9px] text-[#f4b400]">Team Fundraiser</div>
        <Popcorn className="w-6 h-6 text-[#f4b400]" aria-hidden="true" />
      </div>
      <div className="font-display text-xl sm:text-3xl text-white leading-tight mt-1">{settings.title.toUpperCase()}</div>
      <p className="text-[11px] sm:text-xs text-white/70 mt-2">{settings.blurb}</p>
      </div>
      <div className="sm:w-[22rem] shrink-0 flex flex-col">
      {phase === 'coming_soon' && (
        <div className="mt-4 border-l-4 border-[#c8102e] bg-[#0a1f44] px-3 py-2.5 text-white" data-testid="popcorn-coming-soon">
          <div className="flex items-center gap-1.5 font-heading uppercase tracking-[0.3em] text-[9px] text-white/60"><Timer className="w-3 h-3 text-[#f4b400]" /> Coming soon · opens {formatOpen(opens)} ET</div>
          <div className="flex items-end gap-3 mt-1" aria-live="off">
            {[['Days', parts.days], ['Hrs', parts.hours], ['Min', parts.minutes], ['Sec', parts.seconds]].map(([label, value]) => (
              <div key={label} className="text-center min-w-[2.25rem]"><div className="font-display tabular-nums leading-none text-xl sm:text-2xl" data-testid={`popcorn-countdown-${label.toLowerCase()}`}>{pad(value)}</div><div className="font-heading uppercase tracking-widest text-[8px] text-white/55">{label}</div></div>
            ))}
          </div>
        </div>
      )}
      {phase === 'open' && (settings.orderUrl ? (
        <a href={settings.orderUrl} target="_blank" rel="noopener noreferrer" data-testid="popcorn-order-link" className="mt-4 inline-flex items-center justify-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-[10px] px-4 py-3 transition-colors"><ExternalLink className="w-3.5 h-3.5" /> Order Popcorn Now</a>
      ) : (
        <div className="mt-4 bg-[#f4b400] text-[#0a1f44] font-heading uppercase tracking-widest text-[10px] px-4 py-3 text-center" data-testid="popcorn-open-no-link">Sale is open · order link posting shortly</div>
      ))}
      {phase === 'closed' && <div className="mt-4 bg-white/10 text-white/70 font-heading uppercase tracking-widest text-[10px] px-4 py-3 text-center" data-testid="popcorn-closed">Sale closed · thank you, Patriots families!</div>}
      </div>
    </figure>
  );
};
